import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { getIsFeatureHasAnOption } from 'src/utils/featureToggle';
import { getOrderHeaderLabel, getOrderDescription } from 'src/utils/orderUtils';
import { MODULE_NAME } from 'src/constants';
import { isSSR } from 'src/helpers/ssr';
import { LocalizationProvider } from '@lt/localization';

import TourGuide from 'src/components/Banners/TourGuide';

import Menu from './components/HeaderMenu';
import Logo from './components/HeaderLogo';
import SearchWidgetPreview from './components/HeaderSearchWidgetPreview';
import Order from './components/HeaderOrder';
import HeaderSearchForm from './components/HeaderSearchForm';

import type { PropsFromRedux } from '.';

type Props = PropsFromRedux & {
  isHomepage?: boolean;
  isWhiteLabel: boolean;
  shouldFetchClientInfo?: boolean;
  openSidebar?: () => void;
};

const Header = ({
  isMobile,
  isIframe,
  appName,
  orderInfo,
  cigaretteValues,
  pageSearchType,
  isTransparent,
  isFontsBlack,
  isWidgetShown,
  isHomepage,
  isWhiteLabel,
  shouldFetchClientInfo,
  fetchClientInfo,
  initialOpenedField,
  openSidebar,
}: Props) => {
  const [isNewCigarette, setIsNewCigarette] = useState(false);

  useEffect(() => {
    setIsNewCigarette(getIsFeatureHasAnOption('cigarette_type', 'new'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    /*
     * когда Header вложен в Layout (поиск, отель, чекаут), то Layout триггерит INIT_APP,
     * по которому потом триггерится FETCH_CLIENT_INFO -- для других случаев используем shouldFetchClientInfo
     */
    if (shouldFetchClientInfo) {
      fetchClientInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isIframe) return null;

  const isOrderPage = appName === MODULE_NAME.ORDER_CHANGING_REQUEST;
  const isSearchPage = appName === MODULE_NAME.SEARCH;
  const isSeoPage = appName === MODULE_NAME.SEO;

  const isOpenedForm = (isSearchPage || isSeoPage) && !isMobile;

  const Widget = isOrderPage ? (
    <Order
      label={getOrderHeaderLabel(orderInfo)}
      description={getOrderDescription(orderInfo)}
    />
  ) : (
    <SearchWidgetPreview />
  );

  const hasTourGuideBanner = isHomepage && !isMobile && !isWhiteLabel;

  return (
    <LocalizationProvider>
      <StyledHeader
        className="header"
        isMobile={isMobile}
        $isOpenedForm={isOpenedForm}
        $isNewHeader
        isTransparent={isTransparent}
        $isSearchPage={isSearchPage}
        $isNewCigarette={isNewCigarette}
        $isHomepage={isHomepage}
      >
        {isOpenedForm ? (
          <HeaderSearchForm
            initWidget
            isNew
            cigaretteValues={cigaretteValues}
            pageSearchType={pageSearchType}
            initialOpenedField={initialOpenedField}
          />
        ) : (
          <>
            {isSSR ? null : <Logo isHomepage={isHomepage} />}
            {hasTourGuideBanner && <TourGuide isHomeHeader />}
            {isWidgetShown && Widget}
            {isSSR ? null : (
              <StyledMenu
                isCollapsed={!isSearchPage && !isHomepage}
                isTransparent={isTransparent}
                isFontsBlack={isFontsBlack}
                openSidebar={openSidebar}
              />
            )}
          </>
        )}
      </StyledHeader>
    </LocalizationProvider>
  );
};

const StyledHeader = styled.div<
  Pick<Props, 'isMobile' | 'isTransparent'> & {
    $isOpenedForm: boolean;
    $isNewHeader: boolean;
    $isSearchPage: boolean;
    $isNewCigarette: boolean;
    $isHomepage: boolean;
  }
>`
  min-height: ${({ isMobile }) => (isMobile ? '74px' : '82px')};
  ${({ $isHomepage }) => !$isHomepage && 'position: sticky;'};
  z-index: 1000;
  top: 0;
  right: 0;
  left: 0;
  padding: ${({ isMobile, $isNewCigarette }) => {
    if (isMobile) return $isNewCigarette ? '4px' : 0;
    return '8px 25px';
  }};
  display: flex;
  align-items: ${({ $isOpenedForm }) => ($isOpenedForm ? 'stretch' : 'center')};
  color: #fff;
  background-color: ${({ isTransparent }) =>
    isTransparent ? 'transparent' : '#17181a'};
  transform: translateZ(0);

  ${({ $isOpenedForm }) =>
    $isOpenedForm
      ? `flex-direction: column;
        padding: 8px 16px 16px;`
      : ''}
`;

const StyledMenu = styled(Menu)<Pick<Props, 'isTransparent' | 'isFontsBlack'>>`
  margin-left: auto;
  color: ${({ isFontsBlack }) => (isFontsBlack ? '#292929' : '#fff')};

  .messanger_svg__menu-messanger {
    fill: ${({ isFontsBlack }) => (isFontsBlack ? '#292929' : '#fff')};
  }

  & svg {
    stroke: ${({ isFontsBlack }) => (isFontsBlack ? '#292929' : '#fff')};
  }
`;

export default Header;
